import { Permission, PERMISSIONS } from './permissions.const';
import { LegacyRole } from './permissions.types';

/**
 * Permission configuration mapping new permissions to legacy roles.
 *
 * This enables backward compatibility during migration:
 * - New code uses Permission type
 * - Under the hood, checks against user's legacy roles
 *
 * Mapping logic:
 * - operation level -> requires operator or admin
 * - analyst level -> requires analyst, operator, or admin
 * - viewer level -> requires viewer, analyst, operator, or admin
 */
export const permissionConfig: Record<Permission, LegacyRole[]> = {
  // Violations
  [PERMISSIONS.VIOLATION_VIEWER]: ['admin', 'operator', 'analyst', 'viewer'],
  [PERMISSIONS.VIOLATION_ANALYST]: ['admin', 'operator', 'analyst'],
  [PERMISSIONS.VIOLATION_OPERATION]: ['admin', 'operator'],

  // Inventory
  [PERMISSIONS.INVENTORY_VIEWER]: ['admin', 'operator', 'analyst', 'viewer'],
  [PERMISSIONS.INVENTORY_ANALYST]: ['admin', 'operator', 'analyst'],
  [PERMISSIONS.INVENTORY_OPERATION]: ['admin', 'operator'],

  // Agent Steps
  [PERMISSIONS.STEP_VIEWER]: ['admin', 'operator', 'analyst', 'viewer'],
  [PERMISSIONS.STEP_ANALYST]: ['admin', 'operator', 'analyst'],
  [PERMISSIONS.STEP_OPERATION]: ['admin', 'operator'],

  // Findings
  [PERMISSIONS.FINDING_VIEWER]: ['admin', 'operator', 'analyst', 'viewer'],
  [PERMISSIONS.FINDING_ANALYST]: ['admin', 'operator', 'analyst'],
  [PERMISSIONS.FINDING_OPERATION]: ['admin', 'operator'],

  // Policies
  [PERMISSIONS.POLICY_VIEWER]: ['admin', 'operator', 'analyst', 'viewer'],
  [PERMISSIONS.POLICY_ANALYST]: ['admin', 'operator', 'analyst'],
  [PERMISSIONS.POLICY_OPERATION]: ['admin', 'operator'],

  // Playbooks
  [PERMISSIONS.PLAYBOOK_VIEWER]: ['admin', 'operator', 'analyst', 'viewer'],
  [PERMISSIONS.PLAYBOOK_ANALYST]: ['admin', 'operator', 'analyst'],
  [PERMISSIONS.PLAYBOOK_OPERATION]: ['admin', 'operator'],

  // Integrations
  [PERMISSIONS.INTEGRATION_VIEWER]: ['admin', 'operator', 'analyst', 'viewer'],
  [PERMISSIONS.INTEGRATION_OPERATION]: ['admin', 'operator'],

  // Issues
  [PERMISSIONS.ISSUE_VIEWER]: ['admin', 'operator', 'analyst', 'viewer'],
  [PERMISSIONS.ISSUE_OPERATION]: ['admin', 'operator'],

  // Zen AI
  [PERMISSIONS.ZEN_AI_VIEWER]: ['admin', 'operator', 'analyst', 'viewer'],

  // Global
  [PERMISSIONS.GLOBAL_VIEWER]: ['admin', 'operator', 'analyst', 'viewer'],
  [PERMISSIONS.GLOBAL_OPERATOR]: ['admin', 'operator'],
  [PERMISSIONS.GLOBAL_OPERATION]: ['admin', 'operator'],
};

/**
 * Maps new permission levels to legacy role names.
 * Handles "operation" -> "operator" naming difference.
 */
export const levelToRoleMap: Record<string, LegacyRole> = {
  operation: 'operator',
  operator: 'operator',
  analyst: 'analyst',
  viewer: 'viewer',
};

/**
 * Parse a permission string into resource and level.
 */
export function parsePermission(permission: Permission): {
  resource: string;
  level: string;
} {
  const parts = permission.split('_');
  const level = parts.pop() ?? 'viewer';
  const resource = parts.join('_');
  return { resource, level };
}
